import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { addTrainingGroupMember } from "./api";
import { getTrainingGroupId } from "./types";
import type { Club, TrainingGroup } from "./types";
import DialogHeader from "./DialogHeader";
import { MemberContent } from "./MemberListItem";

interface AddGroupMemberDialogProps {
  target: { club: Club; group: TrainingGroup } | null;
  onClose: () => void;
  onSuccess: (groupKey: string) => Promise<void> | void;
  onError: (message: string) => void;
}

const AddGroupMemberDialog: React.FC<AddGroupMemberDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [adding, setAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleAdd = async (handlerId: number) => {
    if (!target) return;
    const trainingGroupId = getTrainingGroupId(target.group);
    if (trainingGroupId == null) return;
    const groupKey = `${target.club.id}-${target.group.group_name}`;
    try {
      setAdding(true);
      await addTrainingGroupMember(trainingGroupId, handlerId);
      await onSuccess(groupKey);
      onClose();
    } catch (err) {
      console.error("Error adding member to training group:", err);
      onError("Failed to add member to training group. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const membersNotInGroup = target
    ? target.club.members.filter(
        (m) =>
          !target.group.members.some((gm) => gm.handler_id === m.handler_id),
      )
    : [];

  const query = searchQuery.toLowerCase();
  const filteredMembers = membersNotInGroup.filter(
    (m) =>
      m.handler_name.toLowerCase().includes(query) ||
      m.email_address.toLowerCase().includes(query),
  );

  return (
    <Dialog
      open={target != null}
      onClose={() => { setSearchQuery(""); onClose(); }}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
    >
      <DialogHeader
        title={`Add Member to ${target?.group.group_name ?? ""}`}
        onClose={() => { setSearchQuery(""); onClose(); }}
        closeDisabled={adding}
      />
      <DialogContent dividers>
        {target && (
          <>
            <TextField
              fullWidth
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <Search sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{ mb: 2 }}
            />
            {membersNotInGroup.length === 0 ? (
              <Alert severity="info">
                All club members are already in this training group.
              </Alert>
            ) : filteredMembers.length === 0 ? (
              <Alert severity="info">
                No members found matching your search.
              </Alert>
            ) : (
              <List sx={{ maxHeight: { xs: "none", sm: 400 }, overflow: "auto" }}>
                {filteredMembers.map((member) => (
                  <ListItemButton
                    key={member.handler_id}
                    onClick={() => handleAdd(member.handler_id)}
                    disabled={adding}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      border: 1,
                      borderColor: "divider",
                      "&:hover": {
                        borderColor: theme.palette.success.main,
                        backgroundColor: theme.palette.success.light + "10",
                      },
                    }}
                  >
                    <MemberContent
                      member={member}
                      avatarColor={theme.palette.success.main}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => { setSearchQuery(""); onClose(); }} disabled={adding}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddGroupMemberDialog;
